import React, { useState, useEffect } from 'react';
import { X, Shield, FileText, AlertTriangle, Building2, CheckCircle2, ChevronRight, MessageSquare, Lock } from 'lucide-react';
import { fetchPolicyDocument, fetchCompanyInfo, PolicyDocument, CompanyLegalInfo } from '../services/legalApi';

export type LegalTabType = 'TERMS' | 'PRIVACY' | 'DISCLAIMER' | 'COMPANY' | 'CHAT_RULES';

interface LegalNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: LegalTabType;
  uiLanguage?: 'ko' | 'en';
  onAgree?: (type: 'TERMS' | 'PRIVACY' | 'DISCLAIMER') => void;
}

export function LegalNoticeModal({
  isOpen,
  onClose,
  initialTab = 'TERMS',
  uiLanguage = 'ko',
  onAgree
}: LegalNoticeModalProps) {
  const [activeTab, setActiveTab] = useState<LegalTabType>(initialTab);
  const [termsDoc, setTermsDoc] = useState<PolicyDocument | null>(null);
  const [privacyDoc, setPrivacyDoc] = useState<PolicyDocument | null>(null);
  const [disclaimerDoc, setDisclaimerDoc] = useState<PolicyDocument | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyLegalInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const isKo = uiLanguage === 'ko';

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchPolicyDocument('TERMS_OF_SERVICE'),
      fetchPolicyDocument('PRIVACY_POLICY'),
      fetchPolicyDocument('FINANCIAL_DISCLAIMER'),
      fetchCompanyInfo()
    ]).then(([terms, privacy, disclaimer, company]) => {
      if (!isMounted) return;
      if (terms) setTermsDoc(terms);
      if (privacy) setPrivacyDoc(privacy);
      if (disclaimer) setDisclaimerDoc(disclaimer);
      if (company) setCompanyInfo(company);
      setLoading(false);
    }).catch(err => {
      console.warn('[LegalNoticeModal] Data fetch error:', err);
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isProHost = typeof window !== 'undefined' && (
    window.location.hostname.includes('ezoptix-pro') ||
    window.location.hostname.startsWith('pro.')
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-4xl max-h-[88vh] bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden text-zinc-200"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-zinc-950/60">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isProHost 
                ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400' 
                : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
            }`}>
              {isProHost ? <Lock className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <span>{isProHost ? (isKo ? 'EZOptix Pro 전용 법률 규정 및 보안 정책' : 'EZOptix Pro Institutional Compliance') : (isKo ? 'EZOptix 서비스 규정 및 법적 고지' : 'EZOptix Legal & Compliance')}</span>
                {isProHost && (
                  <span className="px-2 py-0.5 rounded text-[9px] font-mono bg-cyan-950/80 border border-cyan-800 text-cyan-300">
                    RESTRICTED VIP
                  </span>
                )}
              </h3>
              <p className="text-[11px] text-zinc-400">
                {isProHost 
                  ? (isKo ? '비수탁 주문 프로토콜 및 기관 보안 준수 규정' : 'Non-Custodial & Institutional Compliance') 
                  : (isKo ? '통신판매업 및 자본시장법 준수 운영 규정' : 'E-Commerce & Capital Markets Compliance')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation (5 Tabs matching Image 2) */}
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-white/10 bg-zinc-950/30 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('TERMS')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
              activeTab === 'TERMS'
                ? 'border-emerald-400 text-emerald-400 bg-white/5'
                : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            {isKo ? '서비스 이용약관' : 'Terms of Service'}
          </button>
          <button
            onClick={() => setActiveTab('PRIVACY')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
              activeTab === 'PRIVACY'
                ? 'border-emerald-400 text-emerald-400 bg-white/5'
                : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]'
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            {isKo ? '개인정보처리방침' : 'Privacy Policy'}
          </button>
          <button
            onClick={() => setActiveTab('DISCLAIMER')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
              activeTab === 'DISCLAIMER'
                ? 'border-amber-400 text-amber-400 bg-white/5'
                : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {isKo ? '금융투자 면책고지' : 'Financial Disclaimer'}
          </button>
          <button
            onClick={() => setActiveTab('COMPANY')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
              activeTab === 'COMPANY'
                ? 'border-cyan-400 text-cyan-400 bg-white/5'
                : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            {isKo ? '사업자 신원정보' : 'Company Info'}
          </button>
          <button
            onClick={() => setActiveTab('CHAT_RULES')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
              activeTab === 'CHAT_RULES'
                ? 'border-purple-400 text-purple-400 bg-white/5'
                : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {isKo ? 'AI 챗봇 채팅 규칙' : 'AI Chat Rules'}
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 text-xs leading-relaxed text-zinc-300 font-sans space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-zinc-500">
              <span className="animate-pulse">{isKo ? '법률 고지 문서를 불러오는 중...' : 'Loading policy documents...'}</span>
            </div>
          )}

          {!loading && activeTab === 'TERMS' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              {isProHost ? (
                // ─── Pro 실거래 모드 약관 ───
                <>
                  <div className="p-3.5 rounded-xl bg-cyan-950/40 border border-cyan-800/60 text-cyan-300 text-[11px] leading-relaxed">
                    <strong>[기관 및 화이트리스트 전용]</strong> 본 약관은 EZOptix Pro 프라이빗 터미널 이용에 관한 규정이며, 시스템은 비수탁(Non-Custodial) 방식으로 증권사 공식 OpenAPI를 연동하여 동작합니다.
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-xs text-zinc-300 leading-relaxed bg-black/40 p-4 rounded-xl border border-white/5">
{`[EZOptix Pro 기관 및 적격 투자자 프라이빗 터미널 이용약관]

제1조 (목적)
본 약관은 에이드릭스(Aidrix Inc., 이하 "회사")가 제공하는 EZOptix Pro 폐쇄형 실거래 인텔리전스 터미널의 이용 조건 및 절차를 규정합니다.

제2조 (비수탁 Non-Custodial 원칙)
1. 회사는 이용자의 자금 또는 금융투자상품을 직접 수탁, 보관, 예치, 인출하지 않습니다.
2. 모든 주문 및 계좌 잔고 동기화는 이용자가 등록한 국내외 공인 증권사의 공식 OpenAPI 자격증명을 통해 이용자 본인의 명의와 책임하에 클라우드플레어 엣지 보안 환경에서 직접 실행됩니다.

제3조 (접속 권한 및 보안 의무)
1. 본 서비스는 사전 승인된 화이트리스트 사용자 및 기관 파트너에 한해 접근이 허용됩니다.
2. 이용자는 2FA 인증 수단 및 자격증명을 엄격히 관리해야 하며, 비인가 제3자에게 대여, 양도, 공유하는 행위는 엄격히 금지됩니다.

제4조 (이상 거래 감시 및 차단)
회사는 비정상 트래픽, 분산 서비스 거부(DDoS), 비인가 IP 대역에서의 접근 시도 등 보안 위협 감지 시 즉각 세션을 강제 종료하고 접속을 차단할 수 있습니다.`}
                  </pre>
                </>
              ) : (
                // ─── 모의투자 모드 약관 (자본시장법 / PG 심사 방어) ───
                <>
                  <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[11px] leading-relaxed">
                    <strong>[자본시장법 및 전자상거래법 준수]</strong> EZOptix는 「자본시장과 금융투자업에 관한 법률」상 금융투자업자, 투자자문업자, 유사투자자문업자가 아니며, 순수 모의투자(Sandbox) 시뮬레이션 및 거시경제/퀀트 데이터 시각화 도구(SaaS)만을 제공합니다.
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-xs text-zinc-300 leading-relaxed bg-black/40 p-4 rounded-xl border border-white/5">
{termsDoc?.content || `[EZOptix 모의투자 및 퀀트 데이터 플랫폼 이용약관]

제1조 (목적)
본 약관은 에이드릭스(Aidrix Inc., 이하 "회사")가 운영하는 EZOptix 모의투자 및 퀀트 데이터 서비스(이하 "서비스")의 이용 조건 및 절차를 규정합니다.

제2조 (서비스의 본질 및 비자문 원칙)
1. 본 서비스는 이용자 본인의 독립적인 학습과 판단을 돕기 위한 객관적 금융 데이터 시각화, 모의투자(Sandbox) 체결 시뮬레이션 및 퀀트 지표 연산 도구(SaaS)입니다.
2. 회사는 특정 금융투자상품의 가치나 투자 판단에 대한 조언(자문)을 일절 제공하지 않으며, 이용자의 자산을 일임받아 운용하지 않습니다.
3. 서비스 화면에 표시되는 모든 통계 및 시뮬레이션 결과는 이용자가 직접 설정한 조건 수식에 따른 기계적 계산 결과이며, 투자 권유나 추천이 아닙니다.

제3조 (가상자산 오인 방지 규정)
본 서비스는 「특정 금융거래정보의 보고 및 이용 등에 관한 법률」 및 「가상자산이용자보호법」에 규정된 가상자산(암호화폐/코인)을 일절 취급, 중개, 보관하지 않으며, 공인 증권사의 전통 자본시장 모의투자(VTS) 환경에 국한됩니다.

제4조 (사업자 신원정보)
상호: 에이드릭스 (Aidrix Inc.) / 대표: 이지훈 / 사업자등록번호: 219-19-68192 / 사업장: 서울시 서초구 사임당로

제5조 (디지털 무형 상품 제공 기준 및 유효기간)
1. 본 서비스는 온라인 무형 디지털 소프트웨어(SaaS)로, 결제 완료 즉시 해당 플랜의 이용 권한이 부여됩니다.
2. 모든 유료 서비스의 최종 제공 완료 기간(유효기간)은 결제일로부터 최대 1년 이내입니다.

제6조 (청약철회 및 환불 규정)
1. 회원의 귀책 없이 시스템 장애 등 회사의 귀책 사유로 서비스를 이용하지 못한 경우 결제일로부터 7일 이내 100% 전액 환불됩니다.
2. 결제 후 서비스 이용을 개시하지 않은 경우 7일 이내 청약철회가 가능합니다.
3. 정기구독 및 기간이용권의 중도 해지 시 실제 이용 일수를 공제한 잔여 일할 금액에 대해 부분환불이 가능합니다.`}
                  </pre>
                </>
              )}
            </div>
          )}

          {!loading && activeTab === 'PRIVACY' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[11px] leading-relaxed">
                <strong>[개인정보 보호법 준수]</strong> EZOptix는 증권사 연동 API Key를 단방향/양방향(AES-256) 암호화하여 철저히 격리 보호하며, 회원 탈퇴 시 안전하게 영구 파기합니다.
              </div>
              <pre className="whitespace-pre-wrap font-sans text-xs text-zinc-300 leading-relaxed bg-black/40 p-4 rounded-xl border border-white/5">
                {privacyDoc?.content || `[개인정보처리방침 요약]

제1조 (개인정보의 처리 목적)
회사는 다음의 목적을 위해 최소한의 개인정보만을 처리합니다:
1. 회원 가입 의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증
2. 증권사 공식 OpenAPI 연동을 통한 잔고 및 모의 포트폴리오 데이터 동기화
3. 유료 멤버십 결제 및 환불 처리, 고객 상담 및 부정 이용 방지

제2조 (증권사 자격 증명 보안)
1. 이용자가 등록한 증권사 API Key 및 Secret은 Cloudflare D1 Vault 내에서 AES-256 군사 표준 암호화 알고리즘으로 암호화되어 저장됩니다.
2. 출금 권한이나 계좌 비밀번호 등 자금 인출에 직결되는 정보는 시스템상 일절 수집하거나 저장하지 않습니다.

제3조 (개인정보의 보유 및 이용 기간)
1. 전자상거래법에 따라 계약/청약철회 및 대금결제 기록은 5년간 안전하게 보존됩니다.
2. 회원 탈퇴 시 암호화된 모든 API 연동 키와 식별 정보는 지체 없이 영구 파기됩니다.`}
              </pre>
            </div>
          )}

          {!loading && activeTab === 'DISCLAIMER' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 space-y-2">
                <div className="flex items-center gap-2 font-bold text-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span>{isProHost ? '실거래 시스템 위험 및 이용자 책임 고지' : '자본시장법 준수 및 유사투자자문업 배제 확인'}</span>
                </div>
                <p className="text-xs leading-relaxed text-amber-200/90 font-medium">
                  "{companyInfo?.financialDisclaimerSummary || '본 서비스가 제공하는 데이터 및 지표는 투자 참고용 정보이며, 특정 종목에 대한 투자 권유나 자문이 아니므로 투자 손익에 대한 법적 책임은 사용자 본인에게 있습니다.'}"
                </p>
              </div>

              <div className="bg-black/40 p-5 rounded-xl border border-white/5 space-y-4">
                <h4 className="font-bold text-white text-sm">1. 순수 정량 데이터 시각화 및 모의 시뮬레이터 정체성</h4>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  EZOptix(ezoptix.ai)는 「자본시장과 금융투자업에 관한 법률」상의 투자자문업자, 투자일임업자, 또는 유사투자자문업자가 아닙니다. 시스템이 산출하는 모든 지표, 차트, 신호는 공개된 시장 데이터를 바탕으로 기계적으로 계산된 통계 정보입니다.
                </p>

                <h4 className="font-bold text-white text-sm">2. 과거 데이터 및 백테스팅 통계의 한계</h4>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  과거의 시장 지표 통계치나 시뮬레이션 결과는 미래의 수익률을 보장하지 않습니다. 금융 시장은 예기치 못한 매크로 충격, 유동성 경색, 시장 변동성에 따라 원금 손실 위험이 수반됩니다.
                </p>

                <h4 className="font-bold text-white text-sm">3. 최종 투자 의사결정 및 법적 책임의 귀속</h4>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  이용자는 본 시스템의 모든 산출물을 단순 보조 학습 지표로만 활용해야 합니다. 이용자 본인의 판단하에 행해진 모든 실제 거래, 주문 실행 및 이에 수반되는 손익 결과에 대한 민·형사상 법적 책임은 전적으로 이용자 본인에게 귀속됩니다.
                </p>
              </div>
            </div>
          )}

          {!loading && activeTab === 'COMPANY' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="bg-black/40 p-5 rounded-xl border border-white/5 space-y-3">
                <h4 className="font-bold text-white text-sm border-b border-white/10 pb-2">
                  {isKo ? '전자상거래법상 통신판매업자 신원 정보' : 'E-Commerce Business Information'}
                </h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-zinc-500 font-medium">상호명 / 대표자</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">{companyInfo?.companyName || '에이드릭스 (Aidrix Inc.)'} / {companyInfo?.representative || '이지훈'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">사업자등록번호</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">{companyInfo?.businessRegistrationNumber || '219-19-68192'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">통신판매업 신고번호</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">{companyInfo?.mailOrderSalesRegistrationNumber || '신고 준비 중'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">사업장 소재지</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">{companyInfo?.address || '서울시 서초구 사임당로'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">고객지원 이메일 / 전화</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">
                      {companyInfo?.email || 'ceo@aidrix.io'}
                      {companyInfo?.phone ? ` / ${companyInfo.phone}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">호스팅 서비스 제공자</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">{companyInfo?.hostingProvider || 'Cloudflare, Inc.'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 font-medium">개인정보 보호책임자 (CPO)</dt>
                    <dd className="text-zinc-200 font-semibold mt-0.5">
                      {companyInfo?.cpo?.name || '이지훈'} ({companyInfo?.cpo?.email || 'ceo@aidrix.io'})
                      {companyInfo?.cpo?.phone ? ` / ${companyInfo.cpo.phone}` : ''}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {!loading && activeTab === 'CHAT_RULES' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[11px] leading-relaxed">
                <strong>[AI 챗봇 서비스 운영 및 채팅 규칙 준수]</strong> 본 규정은 인공지능(AI) 챗봇 서비스 이용 시 건전한 사용 질서를 확립하고 법률 위반을 예방하기 위한 준수 규정입니다.
              </div>

              <div className="bg-black/40 p-5 rounded-xl border border-white/5 space-y-4">
                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">1</span>
                    투자자문 및 특정 종목 매매 추천 금지
                  </h4>
                  <p className="text-zinc-400 text-xs leading-relaxed pl-7">
                    AI 챗봇은 「자본시장과 금융투자업에 관한 법률」상 투자자문을 제공하지 않습니다. 특정 종목의 매수·매도 타이밍, 목표가 산정, 추천을 요구하는 질의에 대해서는 답변이 원천적으로 제한됩니다.
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">2</span>
                    금융 민감 정보 및 개인정보 입력 금지
                  </h4>
                  <p className="text-zinc-400 text-xs leading-relaxed pl-7">
                    대화창에 증권사 계좌번호, 비밀번호, 공동인증서 비밀번호, 주민등록번호 등 일체의 민감한 금융 자격증명을 입력해서는 안 되며, 이용자의 부주의로 입력된 정보에 대한 유출 피해는 회사가 책임지지 않습니다.
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">3</span>
                    응답 범위의 객관적 한정
                  </h4>
                  <p className="text-zinc-400 text-xs leading-relaxed pl-7">
                    AI 챗봇은 연준(FRED) 금리, 인플레이션(CPI), VIX 공포지수 등 공개된 거시경제 통계 지표의 개념 해설 및 EZOptix 시스템 기능 사용 방법에 한해서만 객관적인 설명을 제공합니다.
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">4</span>
                    생성형 AI 모델의 한계 및 교차 검증
                  </h4>
                  <p className="text-zinc-400 text-xs leading-relaxed pl-7">
                    생성형 AI 모델의 특성상 일시적으로 사실과 다른 정보(환각 현상)가 포함될 수 있습니다. 중요한 금융 지표 및 경제 일정은 반드시 한국은행, 연준 등 공식 원천 통계 기관의 데이터를 교차 검증하십시오.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-zinc-950/60">
          <div className="text-[11px] text-zinc-500 font-mono">
            {(() => {
              const currentDoc = 
                activeTab === 'TERMS' ? termsDoc :
                activeTab === 'PRIVACY' ? privacyDoc :
                activeTab === 'DISCLAIMER' ? disclaimerDoc : null;
              
              const ver = currentDoc?.version || (activeTab === 'COMPANY' ? companyInfo?.version : null) || '1.0.0';
              const rawEff = currentDoc?.effective_date || (activeTab === 'COMPANY' ? companyInfo?.effectiveDate : null);
              const eff = rawEff ? rawEff.replace(/-/g, '.') : '2026.09.25';
              return `Version ${ver} (Effective ${eff})`;
            })()}
          </div>
          <div className="flex items-center gap-3">
            {onAgree && activeTab !== 'COMPANY' && (
              <button
                onClick={() => onAgree(activeTab as any)}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{isKo ? '확인 및 동의' : 'Acknowledge & Agree'}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white font-medium text-xs rounded-xl border border-white/10 transition-all"
            >
              {isKo ? '닫기' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
